import { Tab, Tabs, Button, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import BubbleChart from '@components/Charts/BubbleChart';
import { useFilter } from '../contexts/FilterContext';

type SkillClusterMapProps = {
  onViewAllSkills?: () => void;
};

type SkillItem = {
  skill: string;
  category: string;
  growthRate: string;
  relativeDemand: number;
  jobRoles: string[];
  sectors?: string[];
  occupations?: string[];
  new?: boolean;
};

const SkillClusterMap: React.FC<SkillClusterMapProps> = ({ onViewAllSkills }) => {
  const { selectedTab } = useFilter();
  const [tabIndex, setTabIndex] = useState(0);
  const [skillsData, setSkillsData] = useState<SkillItem[]>([]);

  useEffect(() => {
    const loadSkillsData = async () => {
      try {
        const skills = await import(`../json/${selectedTab}/skillsData.json`);
        setSkillsData(skills.default || []);
      } catch (error) {
        console.error(`Error loading skills data for tab "${selectedTab}"`, error);
        setSkillsData([]);
      }
    };

    loadSkillsData();
  }, [selectedTab]);

  const parsedData = skillsData.map((skill) => {
    const rate = parseFloat(skill.growthRate.replace('%', ''));
    return {
      name: skill.skill,
      value: skill.relativeDemand,
      growthRate: rate,
    };
  });

  const growing = parsedData.filter((d) => d.growthRate > 0).sort((a,b) => b.value - a.value).slice(0,15);
  const declining = parsedData.filter((d) => d.growthRate < 0).sort((a,b) => b.value - a.value).slice(0,15);
  const newSkills = skillsData
  .filter((skill) => skill.new === true)
  .map((skill) => ({
    name: skill.skill,
    value: skill.relativeDemand,
    growthRate: parseFloat(skill.growthRate.replace('%', '')),
  }))
  .sort((a, b) => b.value - a.value)
  .slice(0, 15);

  return (
    <div className="flex-grow-1 border rounded p-3 bg-white" style={{ minWidth: '300px', flex: 1 }}>
      <div className="d-flex justify-content-between align-items-center pb-4">
        <div>
          <h6 className="mb-1 d-flex align-items-center gap-1" style={{ fontSize: 20 }}>
            Skill Cluster Map
            <Tooltip title="Bubble size shows relative job demand." arrow>
              <InfoOutlined sx={{ fontSize: 16, color: '#6b7280', cursor: 'pointer' }} />
            </Tooltip>
          </h6>
          <p className="text-muted small mb-0">Click a skill to filter the dashboard by related roles. Bubble size shows relative job demand.</p>
        </div>
        <Button
          onClick={onViewAllSkills}
          variant="outlined"
          size="small"
          sx={{
            fontSize: '12px',
            textTransform: 'none',
            paddingX: 2,
            borderRadius: '20px',
            borderColor: '#D7DAE0',
            color: '#043C5B',
            '&:hover': {
              borderColor: '#043C5B',
            },
          }}
        >
          View All Skills &nbsp;&nbsp;
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M11.5556 4.55554L17 9.99999M17 9.99999L11.5556 15.4444M17 9.99999L3 9.99999"
              stroke="#043C5B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </div>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(e, newValue) => setTabIndex(newValue)}
        variant="fullWidth"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          borderBottom: '1px solid #D7DAE0',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: 14,
            color: '#374151',
            borderBottom: '1px solid #D7DAE0',
            borderRadius: 0,
            minWidth: 0,
            '&:hover': {
              color: '#043C5B',
              fontWeight: 600,
              borderBottom: '1px solid #043C5B',
              background: '#F3F5F7',
            },
          },
          '& .Mui-selected': {
            color: '#043C5B',
            fontWeight: 600,
            borderBottom: '1px solid #043C5B',
            background: '#F3F5F7',
          },
        }}
      >
        <Tab label="Growing" />
        <Tab label="Declining" />
        {newSkills.length && <Tab label="New" />}
      </Tabs>

      {/* Tab Content */}
      <div className="pt-3">
        {tabIndex === 0 && (
          <BubbleChart name="skills-bubbles-growing" data={growing} height={550} />
        )}
        {tabIndex === 1 && (
          <BubbleChart name="skills-bubbles-declining" data={declining} height={550} />
        )}
        {tabIndex === 2 && (
          <BubbleChart name="skills-bubbles-new" data={newSkills} height={550} />
        )}
      </div>
    </div>
  );
};

export default SkillClusterMap;
